
import { useState, useEffect, useRef } from "react";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Chart from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getSWCapabilityData } from "../../../services/apiService";
import Swal from "sweetalert2";

Chart.register(annotationPlugin, ChartDataLabels);

const toPercent = (v) => (v == null ? null : Math.round(v * 100));

const LoadingState = () => (
  <div className="min-h-screen bg-[#0f1b2b] flex flex-col items-center justify-center gap-3">
    <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
    <p className="text-gray-400 text-sm">Loading capability data…</p>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="min-h-screen bg-[#0f1b2b] flex flex-col items-center justify-center gap-4">
    <AlertCircle className="w-10 h-10 text-red-400" />
    <p className="text-red-300 text-sm">{message}</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition"
    >
      Retry
    </button>
  </div>
);

const SummaryChart = ({ summaryChart, onVendorClick, onBack }) => {
  const chartRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  const {
    categories = [],
    values = [],
    baseLineValue = 0.6,
    analysisText = "",
  } = summaryChart || {};

  const baselinePct = Math.round(baseLineValue * 100);


  const chartData = {
    labels: categories,
    datasets: [
      {
        label: "SW Capability",
        data: values.map(toPercent),
        backgroundColor: categories.map((v) =>
          v?.toUpperCase().includes("TPV") ? "#9ca3af" : "#3b82f6"
        ),
        borderRadius: 0,
        maxBarThickness: 56,
        datalabels: {
          anchor: "end",
          align: "top",
          offset: 4,
          color: "white",
          font: () => {
            const width = window.innerWidth;

            let size = 15;
            if (width < 640) size = 10;
            else if (width < 1024) size = 12;

            return {
              size: size,
              weight: 600
            };
          },
          formatter: (v) => (v != null ? `${v}%` : ""),
        },
      },
    ]
  };

  useEffect(() => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");

    const getAxisFontSize = () => {
      const width = window.innerWidth;

      if (width < 640) return 10;
      if (width < 1024) return 12;
      return 15;
    };

    const chart = new Chart(ctx, {
      type: "bar",
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements) => {
          if (elements.length === 0) return;
          const idx = elements[0].index;
          const vendor = categories[idx];
          if (!vendor?.toUpperCase().includes("TPV")) {
            onVendorClick({ vendor });
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "black",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "black",
            borderWidth: 1,
            callbacks: {
              title: (items) => items[0].label,
              label: (item) => {
                const val = item.raw;
                return val != null ? `${val}%` : "N/A";
              },
            },
          },
          annotation: {
            annotations: {
              baseline: {
                type: "line",
                yMin: baselinePct,
                yMax: baselinePct,
                borderColor: "#ef4444",
                borderWidth: 4,
                label: {
                  enabled: false,
                },
              },
            },
          },
          datalabels: {
            display: true,
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: "white",
              font: () => ({
                size: getAxisFontSize(),
              }),
            },
          },
          y: {
            min: 0,
            max: 120,
            ticks: {
              stepSize: 20,
              callback: (v) => `${v}%`,
              color: "white",
              padding: 10,
              font: () => ({
                size: getAxisFontSize(),
              }),
            },
            grid: { color: "white", borderDash: [3, 3] },
          },
        }
      },
    });

    chartRef.current = chart;

    return () => chart?.destroy();
  }, [categories, values, baselinePct, onVendorClick]);
  const getResponsiveChartHeight = () => {
    const width = window.innerWidth;

    if (width < 640) return 320;
    if (width < 1024) return 380;
    return 450;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="w-full px-4  sm:px-6 lg:px-8 pt-3 pb-10">

        {/* Header */}
        <div className="flex items-center gap-3 mb-1">
          <button
            onClick={() =>
              navigate("/vendor-capability", { state: { view: "software" } })
            }
            className="p-1 hover:bg-[#1b2a3c] transition"
          >
            <ArrowLeft className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
          </button>

          <h1 className="text-base sm:text-2xl font-bold tracking-tight">
            Monitor Software Capability
          </h1>
        </div>

        {/* Chart Card */}
        <div className="bg-black p-4 sm:p-6 shadow-lg border-t border-[#243448]  ">
          <h2 className="text-center text-sm sm:text-xl font-semibold text-white sm:mb-6 mb-2 sm:mt-5">
            ODMs SW Validation Capability
          </h2>

          <div className="flex justify-center sm:p-0 p-5  sm:-ml-0 -ml-5">
            <div className="h-[300px] sm:h-[400px] w-[120%] sm:w-full max-w-[1500px]">
              <canvas ref={canvasRef} />
            </div>
          </div>
        </div>

        <div className="mt-1 sm:mb-1 mb-3 sm:ml-10 sm:mt-15">
          <h3 className="text-lg sm:text-2xl font-semibold">
            Analysis and Conclusions：
          </h3>
        </div>

        <div className=" text-white text-sm sm:text-base leading-relaxed mb-1 sm:ml-10 sm:mt-8">
          <p className="mb-1 text-sm sm:text-base">
            <span className="font-semibold text-sm sm:text-base">Summary：</span>
            本图表是将每个ODM的软件测试用例的总数与TPV相比，得出每个ODM的软件验证能力分数，
            首次评估是以60%为基准线。
          </p>

          <p>由上述图表可知：</p>
        </div>

        <ol className="list-decimal pl-6  text-sm sm:text-base leading-relaxed sm:ml-10">

          <li>
            <strong>Ostar 和 LnT：</strong>
            软件测试能力已达到合格线，这两家ODM也是目前合作较为紧密的厂商。
            在试跑过程中需要关注问题点的提出和追踪情况，并将发生的 issues
            转化为测试用例，进一步提升软件测试能力。
          </li>

          <li>
            <strong>MTC、Deweco、KTC：</strong>
            这三家ODM中，KTC和MTC拥有丰富TV产品测试经验，
            但对于MNT涉猎较少，软件测试能力仍需提升。
            从目前MNT开案情况来看，仅试跑过一款，
            优先级：一般；Deweco目前无MNT案子，优先级：低。
          </li>

          <li>
            <strong>Hengfakj、CTX：</strong>
            CTX主要承接Philips机种，在2025年出现失误，
            验证过程漏失，需要重点关注软件测试进度及测试用例完整度。
            Hengfakj测试项目完整度较高，但测试详细程度
            没有细化到测试用例中，需要加强测试用例方面，优先级：高。
          </li>

          <li>
            <strong>Hannovo：</strong>
            软件测试能力较弱，目前暂无案子，优先级：一般。
          </li>

        </ol>
      </div>
    </div>
  );
};

const VendorDetailChart = ({ vendorName, vendorChart, onBack }) => {
  const chartRef = useRef(null);
  const canvasRef = useRef(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const {
    title = `${vendorName} Function proportion`,
    functionCategories = [],
    values = [],
    baseLines = [],
    analysisText = "",
  } = vendorChart || {};

  const lineColors = ["#f97316", "#eab308", "#22d3ee", "#a78bfa"];

  const getResponsiveFont = (desktop = 15, tablet = 13, mobile = 11) => {
    const width = window.innerWidth;

    if (width < 640) return mobile;
    if (width < 1024) return tablet;
    return desktop;
  };


  const getResponsiveFontSize = (desktop = 10, tablet = 9, mobile = 8) => {
    const width = window.innerWidth;

    if (width < 640) return mobile;
    if (width < 1024) return tablet;
    return desktop;
  };
  const chartData = {
    labels: functionCategories,
    datasets: [
      ...baseLines.map((bl, i) => ({
        type: "line",
        label: bl.label,
        data: functionCategories.map((fn) => toPercent(bl.values?.[fn] ?? null)),
        borderColor: lineColors[i % lineColors.length],
        backgroundColor: lineColors[i % lineColors.length],
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0,
        spanGaps: true,
        order: 1,
      })),
      {
        type: "bar",
        label: vendorName,
        data: values.map(toPercent),
        backgroundColor: "#3b82f6",
        borderRadius: 4,
        maxBarThickness: 56,
        datalabels: {
          anchor: "end",
          align: "top",
          offset: 4,
          color: "#e2e8f0",
          font: () => ({
            size: getResponsiveFont(15, 13, 11),
            weight: 600
          }),
          formatter: (v) => (v != null ? `${v}%` : ""),
        },
        order: 2,
      },
    ],
  };


  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;


    const allValues = [
      ...values.map(toPercent),
      ...baseLines.flatMap((bl) =>
        functionCategories.map((fn) => toPercent(bl.values?.[fn] ?? 0))
      ),
    ];

    const validValues = allValues.filter((v) => v != null && !isNaN(v));
    const maxValue = validValues.length ? Math.max(...validValues) : 0;

    const getStepSize = (max) => {
      if (max <= 90) return 10;
      if (max <= 180) return 20;
      if (max <= 250) return 50;
      if (max <= 500) return 100;
      return 200;
    };

    const stepSize = getStepSize(maxValue);

    const roundedMax = Math.ceil(maxValue / stepSize) * stepSize;

    const yAxisMax = roundedMax + stepSize;

    const ctx = canvasRef.current.getContext("2d");

    const chart = new Chart(ctx, {
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {

          legend: {
            position: "bottom",
            labels: {
              color: "#94a3b8",
              font: () => ({
                size: getResponsiveFont(15, 13, 11)
              }),
              padding: 16,
              boxWidth: 100,

              sort: (a, b) => {
                const getPriority = (label) => {
                  if (label === vendorName) return 0;
                  if (label.toLowerCase().includes("base")) return 1;
                  return 2;
                };

                return getPriority(a.text) - getPriority(b.text);
              }
            }
          },
          tooltip: {
            mode: "index",
            intersect: false,
            backgroundColor: "#0f1b2b",
            titleColor: "#d1d5db",
            bodyColor: "#d1d5db",
            borderColor: "#2a3f55",
            borderWidth: 1,
            callbacks: {
              label: (item) => {
                const val = item.raw;
                return `${item.dataset.label}: ${val != null ? val + "%" : "N/A"
                  }`;
              },
            },
          },
          datalabels: {
            display: (context) => context.dataset.type === "bar",
          },
        },

        scales: {
          x: {
            ticks: {
              color: "#94a3b8",
              font: () => ({
                size: getResponsiveFont(15, 13, 11)
              })
            },
            grid: { display: false },
          },

          y: {
            min: 0,
            max: yAxisMax,
            ticks: {
              stepSize: stepSize,
              callback: (v) => `${Number(v).toFixed(2)}%`,
              color: "#94a3b8",
              font: () => ({
                size: getResponsiveFont(15, 13, 11)
              }),
            },
            grid: {
              color: "white",
              borderDash: [3, 3],
            },
          }
        },
      },
    });

    chartRef.current = chart;

    return () => chart?.destroy();
  }, [functionCategories, values, baseLines, vendorName, lineColors, screenWidth]);
  const getResponsiveChartHeight = () => {
    const width = window.innerWidth;

    if (width < 640) return 390;
    if (width < 1024) return 380;
    return 450;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="w-full px-4 sm:px-6 pt-6 pb-10">
        <div className="flex items-center gap-3 mb-1">
          <button
            onClick={onBack}
            className="p-1 hover:bg-[#1b2a3c] transition"
          >
            <ArrowLeft className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
          </button>

          <h1 className="text-base sm:text-2xl font-bold tracking-tight">
            Monitor Software Capability
          </h1>
        </div>

        <div className="bg-black p-4 sm:p-6 mb-5 sm:mt-0 -mt-6 text-center shadow-lg border-t border-[#243448]">
          <h2 className="text-center text-sm sm:text-xl font-semibold text-white mb-5">
            {title}
          </h2>
          <div className="flex justify-center">
            <div
              style={{
                height: getResponsiveChartHeight(),
                width: "100%",
                maxWidth: 1500
              }}
            >
              <canvas ref={canvasRef} />
            </div>
          </div>
        </div>
        <div className="w-full flex justify-center -mt-5  sm:mb-5">
          <div className="bg-black  p-4 sm:p-1 border border-black w-full max-w-[1500px]">
            <p className="text-xs sm:text-lg font-bold mb-1 text-white">
              Remark：
            </p>

            <p className="text-xs sm:text-lg text-white leading-relaxed">
              The percentage of each function on the chart is the number of test
              cases for the corresponding function module of the ODM / the number
              of test cases for the corresponding function module of the TPV.
            </p>
          </div>
        </div>

        {analysisText && (
          <div className="bg-black rounded-2xl sm:p-10  border border-black  ">
            <h3 className="text-sm sm:text-2xl font-semibold mb-2">
              Analysis and Conclusions：
            </h3>
            <p className="text-xs sm:text-sm text-white leading-relaxed whitespace-pre-line">
              {analysisText}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default function PDAIOSoftwareCapability() {
  const navigate = useNavigate();

  const [view, setView] = useState("summary");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getSWCapabilityData({
        category: "Vendor Capability",
        sub_category: "software",
        productType: "PD_AIO",
      });
      setApiData(result?.data ?? result);
    }
    catch (err) {
      console.error("API Error:", err);
    
      if (err.response?.status === 404) {
        Swal.fire({
          icon: "warning",
          title: "Template Missing",
          text: "File needs to be uploaded in Template Management",
          confirmButtonText: "OK",
          confirmButtonColor: "#2563eb",
          background: "white",
          color: "black"
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/vendor-capability", { state: { view: "software" } });
          }
        });
    
        return;
      }
    
      // fallback for other errors
      setError(err?.message ?? "Failed to load data");
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={fetchData} />;
  if (!apiData) return <ErrorState message="No data returned from server." onRetry={fetchData} />;

  if (view === "vendor-detail" && selectedVendor) {
    return (
      <VendorDetailChart
        vendorName={selectedVendor}
        vendorChart={apiData.vendorCharts?.[selectedVendor] ?? {}}
        onBack={() => {
          setSelectedVendor(null);
          setView("summary");
        }}
      />
    );
  }

  return (
    <SummaryChart
      summaryChart={apiData.summaryChart ?? {}}
      onVendorClick={(entry) => {
        setSelectedVendor(entry.vendor);
        setView("vendor-detail");
      }}
      onBack={() => navigate(-1)}
    />
  );
}